import { z } from 'zod';
import { getContextProviderExtensionsDir } from '@wix/cli-astro-definitions';
import {
  folderExistsMessage,
  pathAvailableValidation,
  VALID_PATH_REGEX,
} from '../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { enrichWithDerivedFolder } from '../utils/derive-folder.js';
import type { WithDerivedFolder } from '../utils/derive-folder.js';
import { generateContextProvider } from './generate.js';

const schema = z.object({
  extensionType: z.literal('CONTEXT_PROVIDER'),
  name: z.string().describe(defineField({ title: 'Provider name' })),
  folder: z
    .string()
    .regex(VALID_PATH_REGEX)
    .optional()
    .describe(
      defineField({
        title: 'Provider folder',
        description:
          'Lowercase alphanumeric, hyphens, and slashes. Derived from the provider name when omitted.',
      })
    ),
  type: z
    .string()
    .min(1)
    .describe(
      defineField({
        title: 'Provider type',
        description:
          'The context provider type suffix. The project code identifier is prepended automatically.',
      })
    ),
});

export const contextProvider: ExtensionDef<
  typeof schema,
  WithDerivedFolder<typeof schema>
> = {
  title: 'Context Provider',
  description: 'An experimental React context provider',
  group: 'Site',
  zodSchema: schema,
  enrich: (data) => enrichWithDerivedFolder(data, { sourceField: 'name' }),
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getContextProviderExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : folderExistsMessage('context provider', data.folder, 'name');
  },
  generate: ({ projectFolder, model, data }) =>
    generateContextProvider(
      projectFolder,
      data.folder,
      data.name,
      data.type,
      model.config.projectType === 'App'
        ? model.config.codeIdentifier
        : undefined
    ),
};
